<!-- ABOUTME: Email template form component for creating and editing templates -->
<!-- ABOUTME: Uses MultiLanguageEmailForm component and handles template-specific fields -->
<template>
  <div class="max-w-6xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 mb-2">
        {{ templateId ? 'Edit' : 'New' }} Email Template
      </h1>
      <p class="text-gray-600">{{ templateId ? 'Update the email template content and settings' : 'Create a reusable email template for automation rules' }}</p>
    </div>

    <!-- Error Display -->
    <div v-if="errors.length > 0" class="mb-6 bg-red-50 border-l-4 border-red-500 rounded-md p-4">
      <h4 class="text-sm font-semibold text-red-800 mb-2">
        {{ errors.length }} error(s) prohibited this template from being saved:
      </h4>
      <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
        <li v-for="(error, idx) in errors" :key="idx">{{ error }}</li>
      </ul>
    </div>

    <!-- Main Form -->
    <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <!-- Template Key and Title -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Template Key <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            v-model="templateKey"
            :disabled="templateId !== null"
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 transition-colors focus:border-primary focus:outline-none focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="e.g., onboarding_day_2">
          <p class="text-xs text-gray-500 mt-1">
            Unique identifier for this template (snake_case). Used by automation rules.
            {{ templateId ? 'Cannot be changed after creation.' : '' }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Template Title
          </label>
          <input
            type="text"
            v-model="templateTitle"
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 transition-colors focus:border-primary focus:outline-none focus:ring-primary"
            placeholder="e.g., &quot;Onboarding Day 2&quot;">
          <p class="text-xs text-gray-500 mt-1">Internal name for this template</p>
        </div>
      </div>

      <!-- Multi-language Email Editor -->
      <MultiLanguageEmailForm
        ref="multiLangEditor"
        :email-content-id="templateId"
        :initial-variants="initialVariants"
        :templates="[]"
        :show-template-selector="false"
        :save-url="saveUrl"
        email-type="template"
        @saved="handleTemplateSaved"
        @error="handleTemplateError"
      />

      <!-- Form Actions -->
      <div class="mt-6 flex flex-col gap-4 pt-6 border-t border-gray-200">
        <!-- Preview Status Message -->
        <div v-if="multiLangEditor?.previewMessage" class="text-sm" :class="{
          'text-green-500': multiLangEditor?.previewMessageClass === 'preview-success',
          'text-red-500': multiLangEditor?.previewMessageClass === 'preview-error',
          'text-amber-500': multiLangEditor?.previewMessageClass === 'preview-pending'
        }">
          {{ multiLangEditor.previewMessage }}
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-3 flex-wrap">
          <button
            @click="sendPreview"
            :disabled="!canSendPreview || multiLangEditor?.previewLoading"
            type="button"
            class="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            :title="canSendPreview ? 'Send test email to yourself' : 'Fill in subject and body to send preview'">
            {{ multiLangEditor?.previewLoading ? 'Sending Preview...' : 'Send Preview' }}
          </button>

          <button
            @click="saveTemplate"
            :disabled="isSaving"
            type="button"
            class="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-dark focus:outline-none focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
            {{ isSaving ? 'Saving...' : (templateId ? 'Update Template' : 'Create Template') }}
          </button>

          <a
            :href="cancelUrl"
            class="px-4 py-2 text-gray-700 text-sm font-medium hover:text-gray-900 transition-colors cursor-pointer">
            Cancel
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import axios from 'axios'
import MultiLanguageEmailForm from './MultiLanguageEmailForm.vue'

const props = defineProps({
  templateId: {
    type: [String, Number],
    default: null
  },
  initialTemplateKey: {
    type: String,
    default: ''
  },
  initialTitle: {
    type: String,
    default: ''
  },
  initialVariants: {
    type: Object,
    default: () => ({})
  },
  saveUrl: {
    type: String,
    default: '/admin/email_templates'
  },
  cancelUrl: {
    type: String,
    default: '/admin/email_templates'
  }
})

// State
const templateKey = ref(props.initialTemplateKey)
const templateTitle = ref(props.initialTitle)
const errors = ref([])
const isSaving = ref(false)
const multiLangEditor = ref(null)

// Computed property to check if preview can be sent
const canSendPreview = computed(() => {
  return multiLangEditor.value?.canSendPreview || false
})

// Get CSRF token
function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}

// Handle template saved
function handleTemplateSaved(newTemplateId) {
  console.log('Template content saved:', newTemplateId)
}

// Handle template error
function handleTemplateError(error) {
  errors.value = [error]
}

// Send preview email
function sendPreview() {
  if (multiLangEditor.value && multiLangEditor.value.sendPreview) {
    multiLangEditor.value.sendPreview()
  }
}

// Save template
async function saveTemplate() {
  errors.value = []

  // Validate template key (only for new templates - existing templates have disabled key field)
  if (!props.templateId) {
    if (!templateKey.value || !templateKey.value.trim()) {
      errors.value = ['Template key is required']
      return
    }

    // Validate template key format (snake_case)
    if (!/^[a-z][a-z0-9_]*$/.test(templateKey.value)) {
      errors.value = ['Template key must be in snake_case format (lowercase letters, numbers, and underscores only)']
      return
    }
  }

  // Validate email content
  if (multiLangEditor.value && !multiLangEditor.value.validate()) {
    errors.value = ['Please fill in at least one language variant (subject and body)']
    return
  }

  // Get all variants from child component
  const allVariants = multiLangEditor.value ? multiLangEditor.value.getAllVariants() : {}

  // Transform variants to Rails nested attributes format
  const variants_attributes = Object.entries(allVariants)
    .filter(([locale, data]) => {
      // Only include variants that have actual content
      return data.subject && data.subject.trim() && data.body && data.body.trim()
    })
    .map(([locale, data]) => {
      const attr = {
        locale: locale,
        subject: data.subject,
        body: data.body
      }

      // Only include id if it exists (for existing variants)
      if (data.id) {
        attr.id = data.id
      }

      return attr
    })

  isSaving.value = true

  // Build URL
  const url = props.templateId
    ? `${props.saveUrl}/${props.templateId}`
    : props.saveUrl

  try {
    const method = props.templateId ? 'patch' : 'post'

    console.log(`Saving template: ${method.toUpperCase()} ${url}`)
    console.log('Template data:', { templateKey: templateKey.value, title: templateTitle.value })
    console.log('Variants to save:', variants_attributes)

    const response = await axios[method](url, {
      email_content: {
        template_key: templateKey.value,
        title: templateTitle.value,
        variants_attributes: variants_attributes
      }
    }, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (response.data.success || response.data.redirect_url) {
      window.location.href = response.data.redirect_url || props.cancelUrl
    } else {
      errors.value = response.data.errors || ['Failed to save template']
    }
  } catch (error) {
    console.error('Save template error:', error)
    if (error.response?.status === 404) {
      errors.value = [`Save failed: URL not found (${url}). Please contact support.`]
    } else if (error.response?.data?.errors) {
      errors.value = error.response.data.errors
    } else if (error.response?.data?.error) {
      errors.value = [error.response.data.error]
    } else {
      errors.value = [`Failed to save template: ${error.message || 'Unknown error'}`]
    }
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.bg-primary {
  background-color: #17B7B7;
}

.hover\:bg-primary-dark:hover {
  background-color: #0fa8a8;
}

.focus\:ring-primary:focus {
  --tw-ring-color: #17B7B7;
}

.focus\:border-primary:focus {
  border-color: #17B7B7;
}
</style>
